import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ArrowLeft } from 'lucide-react';
import { notificationChannelsApi } from '../../services/notification-api';
import { useChatLayout } from '../../components/layout/ChatLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { NotificationChannelForm } from '../../components/notifications/NotificationChannelForm';
import { NotificationChannelList } from '../../components/notifications/NotificationChannelList';
import toast from 'react-hot-toast';
import { handleApiError } from '../../services/api';
import type {
  NotificationChannel,
  NotificationChannelFilters,
  NotificationChannelType,
  CreateNotificationChannelDto,
  UpdateNotificationChannelDto,
} from '../../types/notifications';

export function NotificationChannelsPage() {
  const { isMobile, toggleSidebar } = useChatLayout();
  const navigate = useNavigate();
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<NotificationChannelFilters>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<NotificationChannel | null>(null);

  useEffect(() => {
    loadChannels();
  }, [filters]);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const response = await notificationChannelsApi.getUserChannels(filters);
      setChannels(response.channels || []);
    } catch (error) {
      console.error('Failed to load channels:', error);
      toast.error('Failed to load notification channels');
      setChannels([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async (data: CreateNotificationChannelDto) => {
    try {
      const response = await notificationChannelsApi.createChannel(data);
      toast.success(response.message);
      setShowCreateModal(false);
      loadChannels();
    } catch (error) {
      toast.error(handleApiError(error) || 'Failed to create channel');
    }
  };

  const handleUpdateChannel = async (id: string, data: UpdateNotificationChannelDto) => {
    try {
      const response = await notificationChannelsApi.updateChannel(id, data);
      toast.success(response.message);
      setEditingChannel(null);
      loadChannels();
    } catch {
      toast.error('Failed to update channel');
    }
  };

  const handleDeleteChannel = async (id: string) => {
    if (!confirm('Are you sure you want to delete this notification channel?')) {
      return;
    }

    try {
      const response = await notificationChannelsApi.deleteChannel(id);
      toast.success(response.message);
      loadChannels();
    } catch {
      toast.error('Failed to delete channel');
    }
  };

  const handleVerifyChannel = async (channel: NotificationChannel) => {
    try {
      const response = await notificationChannelsApi.verifyChannel(channel.id);
      toast.success(response.message);
      loadChannels();
    } catch {
      toast.error('Failed to verify channel');
    }
  };

  const handleTestChannel = async (channel: NotificationChannel) => {
    try {
      const response = await notificationChannelsApi.testChannel(channel.id);
      toast.success(response.message);
    } catch {
      toast.error('Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Settings
            </button>
            <h1 className="text-2xl font-bold text-foreground">Notification Channels</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage how you receive notifications from Zamino
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors"
          >
            Add Channel
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl bg-muted border border-border">
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-4">
              <select
                className="rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors"
                value={filters.channel_type || ''}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    channel_type: (e.target.value as NotificationChannelType) || undefined,
                  })
                }
              >
                <option value="">All Types</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="discord">Discord</option>
              </select>

              <select
                className="rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors"
                value={filters.is_active === undefined ? '' : String(filters.is_active)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    is_active: e.target.value === '' ? undefined : e.target.value === 'true',
                  })
                }
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <select
                className="rounded-xl text-sm bg-white dark:bg-white/5 border border-input dark:border-white/10 text-foreground px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-primary/40 transition-colors"
                value={filters.is_verified === undefined ? '' : String(filters.is_verified)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    is_verified: e.target.value === '' ? undefined : e.target.value === 'true',
                  })
                }
              >
                <option value="">All Verification</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>

              <button
                onClick={() => setFilters({})}
                className="px-3 py-1.5 text-sm font-medium rounded-lg text-muted-foreground hover:bg-accent dark:hover:bg-white/10 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Channel List */}
          <NotificationChannelList
            channels={channels}
            onEdit={setEditingChannel}
            onDelete={handleDeleteChannel}
            onVerify={handleVerifyChannel}
            onTest={handleTestChannel}
          />
        </div>

        {/* Create Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        >
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Add Notification Channel</h2>
            <NotificationChannelForm
              onSubmit={(data) => handleCreateChannel(data as CreateNotificationChannelDto)}
              onCancel={() => setShowCreateModal(false)}
            />
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={!!editingChannel}
          onClose={() => setEditingChannel(null)}
        >
          {editingChannel && (
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Edit Notification Channel</h2>
              <NotificationChannelForm
                channel={editingChannel}
                onSubmit={(data) => handleUpdateChannel(editingChannel.id, data as UpdateNotificationChannelDto)}
                onCancel={() => setEditingChannel(null)}
              />
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}